import { useWindowOpen, useWorkingPlatform } from '@repo/platform';
import { useUserEventLogger } from '@repo/tracking-service';
import { useDialog } from '@repo/ui';
import { Button, Fieldset, TextField } from '@zenterprise-inc/ui';
import { SyntheticEvent, useEffect, useState } from 'react';

import { ErrorType } from '@/__generated__/checkHometaxAccountMutation.graphql';
import IllustHometax from '@/assets/illust-icons/illust-hometax.svg';
import { getCorporationRefundData } from '@/components/tax-refund/common/data/corporation-refund-data';
import { 홈택스아이디비번찾기주소, 홈택스아이디생성주소, 홈택스로그인주소 } from '@/lib/constants/link';
import { useCheckHometaxAccount } from '@/lib/hooks/api/mutation/use-check-hometax-account';

const CorporationHometaxAuthContent = () => {
  const { openWindow } = useWindowOpen();
  const { isPartnerPlatform } = useWorkingPlatform();

  const [id, setId] = useState('');
  const [password, setPassword] = useState('');
  const [registNumFront, setRegistNumFront] = useState('');
  const [registNumBack, setRegistNumBack] = useState('');
  const [disableButton, setDisableButton] = useState(true);
  const [birthDateError, setBirthDateError] = useState(false);
  const [registNumLengthError, setRegistNumLengthError] = useState(false);

  const { showDialog } = useDialog();
  const { handleCheckHometaxAccount, isInFlight } = useCheckHometaxAccount();

  const { sendViewEvent, sendClickEvent } = useUserEventLogger();

  const refundData = getCorporationRefundData();

  useEffect(() => {
    if (refundData?.hometaxId) {
      setId(refundData.hometaxId);
    }
  }, [refundData?.hometaxId]);

  useEffect(() => {
    if (id !== '' && password !== '' && registNumFront !== '' && registNumBack !== '' && !birthDateError && !registNumLengthError) {
      setDisableButton(false);
    } else {
      setDisableButton(true);
    }
  }, [id, password, registNumFront, registNumBack, birthDateError, registNumLengthError]);

  const handleChangeId = (e: SyntheticEvent<HTMLInputElement>) => {
    const {
      currentTarget: { value }
    } = e;
    setId(value.replaceAll(' ', ''));
  };

  const clickHometaxId = () => {
    sendClickEvent('corps-hometax_login_id');
  };

  const handleChangePassword = (e: SyntheticEvent<HTMLInputElement>) => {
    const {
      currentTarget: { value }
    } = e;
    setPassword(value.replaceAll(' ', ''));
  };

  const clickPassword = () => {
    sendClickEvent('corps-hometax_login_pw');
  };

  const handleChangeRegistNumFront = (e: SyntheticEvent<HTMLInputElement>) => {
    const {
      currentTarget: { value }
    } = e;
    const numericValue = value.replace(/[^0-9]/g, '').slice(0, 6);

    // 유효한 생년월일 검증
    let isValid = true;
    if (numericValue.length >= 4) {
      const year = parseInt(numericValue.slice(0, 2));
      const month = parseInt(numericValue.slice(2, 4));

      if (year < 0 || year > 99) {
        isValid = false;
      }
      if (month < 1 || month > 12) {
        isValid = false;
      }
      if (numericValue.length >= 6) {
        const day = parseInt(numericValue.slice(4, 6));
        if (day < 1 || day > 31) {
          isValid = false;
        }
      }
    }

    setBirthDateError(!isValid && numericValue.length >= 4);
    setRegistNumFront(numericValue);
  };

  const handleChangeRegistNumBack = (e: SyntheticEvent<HTMLInputElement>) => {
    const {
      currentTarget: { value }
    } = e;
    const numericValue = value.replace(/[^1-6]/g, '').slice(0, 1);
    setRegistNumBack(numericValue);
  };

  const clickRegistNum = () => {
    sendClickEvent('corps-hometax_login_regist-num');
  };

  const handleBlurRegistNum = () => {
    if (registNumFront.length > 0 && registNumFront.length < 6) {
      setRegistNumLengthError(true);
    } else {
      setRegistNumLengthError(false);
    }
  };

  const handleClickFindeHometaxIDPassword = () => {
    sendClickEvent('corps-hometax_login_find');
    openWindow(홈택스아이디비번찾기주소);
  };

  const handleClickCreateHometaxID = () => {
    sendClickEvent('corps-hometax_signup');
    openWindow(홈택스아이디생성주소);
  };

  // 에러 다이얼로그 처리 함수
  const handleShowErrorDialog = async (errorType: ErrorType | null | undefined) => {
    let title = '';
    let description: string | undefined = '';
    let primaryText = '확인';
    let secondaryText: string | undefined = undefined;
    let onPrimary: (() => void) | undefined;
    let onSecondary: (() => void) | undefined;
    let errorAttribute: string = '';

    switch (errorType) {
      case 'ERR_HOMETAX_PASSWORD':
        title = '로그인을 실패했어요';
        description = '입력한 정보가 맞는지 다시 확인해주세요.';
        onPrimary = () => {
          sendClickEvent('corps-hometax_error_close');
        };
        errorAttribute = 'idpw';
        break;
      case 'ERR_HOMETAX_BLOCK_ID':
        title = '입력한 계정이 일시 잠금됐어요';
        description = '잘못된 정보로 여러 차례 시도하여 계정이 일시 잠금됐어요. 홈택스 [아이디/비밀번호 찾기]를 통해 올바른 정보를 찾아 다시 시도해주세요.';
        primaryText = '홈택스로 이동';
        secondaryText = '다시 입력하기';
        onPrimary = () => {
          sendClickEvent('corps-hometax_error_move-hometax');
          window.open(홈택스아이디비번찾기주소, '_blank');
        };
        onSecondary = () => {
          sendClickEvent('corps-hometax_error_refill');
        };
        errorAttribute = 'pw-five';
        break;
      case 'ERR_HOMETAX_PASSWORD_CHANGE_REQUEST':
        title = '입력한 계정의 비밀번호 변경을 홈택스에서 요청했어요';
        description = '개인정보 보호를 위해 홈택스에서 3개월 이상 변경되지 않은 비밀번호 확인을 요청했어요. 홈택스로 이동해 계정 상태를 확인해주세요.';
        primaryText = '홈택스로 이동';
        onPrimary = () => {
          sendClickEvent('corps-hometax_error_move-hometax');
          window.open(홈택스로그인주소, '_blank');
        };
        errorAttribute = 'pw-change';
        break;
      case 'ERR_HOMETAX_NO_CORPS_ID':
        title = '법인 홈택스 계정을 연동해주세요';
        description = '입력한 홈택스 계정이 법인사업자가 아니에요. 계정을 다시 확인해주세요.';
        onPrimary = () => {
          sendClickEvent('corps-hometax_error_close');
        };
        errorAttribute = 'no-corp-id';
        break;
      case 'ERR_EXIST_SEARCH_HISTORY':
        title = '다른 법인사업자 홈택스 계정을 입력해주세요';
        description = '이미 내 사업체 목록에 있는 사업장의 홈택스 계정을 입력하셨어요. 다른 계정을 입력해주세요.';
        onPrimary = () => {
          sendClickEvent('corps-hometax_error_close');
        };
        errorAttribute = 'search-history';
        break;
      case 'ERR_NO_EQUAL_TIN': {
        const maxBizNameLength = 14;
        const bizName = refundData?.bizName
          ? refundData.bizName.length > maxBizNameLength
            ? refundData.bizName.slice(0, maxBizNameLength - 1) + '...'
            : refundData.bizName
          : '';
        title = `'${bizName}'의 홈택스 계정을 입력해주세요`;
        description = '해당 사업체의 홈택스 계정으로만 조회할 수 있어요.';
        onPrimary = () => {
          sendClickEvent('corps-hometax_error_close');
        };
        errorAttribute = 'no-equal-tin';
        break;
      }
      default:
        title = '홈택스 로그인에 문제가 발생했어요';
        description = (errorType as string) ?? '';
        onPrimary = () => {
          sendClickEvent('corps-hometax_error_close');
        };
        errorAttribute = 'etc';
        break;
    }

    sendViewEvent(
      'corps-hometax_error',
      {
        error: errorAttribute
      },
      { isAllowRetry: true }
    );

    await showDialog({
      title,
      description,
      buttons: {
        primary: {
          text: primaryText,
          onClick: onPrimary
        },
        ...(secondaryText
          ? {
              secondary: {
                text: secondaryText,
                onClick: onSecondary
              }
            }
          : {})
      }
    });
  };

  const handleClickNext = () => {
    sendClickEvent('corps-hometax_login_next');
    handleCheckHometaxAccount({
      variables: {
        corpType: 'CORPS',
        loginType: 'HometaxId',
        hometaxAccount: {
          id,
          password,
          resno: registNumFront + registNumBack
        },
        tin: refundData?.tin
      },
      errorAction: (errorType) => {
        handleShowErrorDialog(errorType);
      }
    });
  };

  return (
    <div className="page-padding flex w-full flex-col pb-24">
      <div className="mb-9 flex justify-between">
        <div>
          <div className="text-22 mb-3 font-bold text-gray-900">
            환급액 조회를 위해
            <br />
            홈택스 로그인이 필요해요
          </div>
          <div className="text-gray-700">조회할 사업체의 홈택스 정보를 입력해주세요.</div>
        </div>
        <div>
          <IllustHometax width={64} height={64} />
        </div>
      </div>

      <Fieldset.Wrapper className="mb-5 w-full">
        <Fieldset.Label>홈택스 아이디</Fieldset.Label>
        <TextField.Basic className="w-full" type="text" value={id} onChange={handleChangeId} onClick={clickHometaxId} />
      </Fieldset.Wrapper>

      <Fieldset.Wrapper className="mb-5 w-full">
        <Fieldset.Label>홈택스 비밀번호</Fieldset.Label>
        <TextField.Password className="w-full" type="text" value={password} onChange={handleChangePassword} onClick={clickPassword} />
        <Fieldset.Help>영문·숫자·특수문자 조합, 9~15자리</Fieldset.Help>
      </Fieldset.Wrapper>

      <Fieldset.Wrapper className="w-full" isInvalid={birthDateError || registNumLengthError}>
        <Fieldset.Label>주민등록번호</Fieldset.Label>
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <TextField.Basic
              className="w-full"
              type="text"
              value={registNumFront}
              onChange={handleChangeRegistNumFront}
              onClick={clickRegistNum}
              onBlur={handleBlurRegistNum}
              maxLength={6}
              inputMode="numeric"
              error={birthDateError || registNumLengthError}
            />
          </div>
          <span className="text-gray-500 flex-shrink-0">-</span>
          <div className="flex-1 flex items-center gap-2 min-w-0">
            <TextField.Basic
              className="w-11 flex-shrink-0"
              type="text"
              value={registNumBack}
              onChange={handleChangeRegistNumBack}
              onClick={clickRegistNum}
              onBlur={handleBlurRegistNum}
              maxLength={1}
              inputMode="numeric"
            />
            <span className="text-gray-400 text-sm flex-shrink-0">••••••</span>
          </div>
        </div>
        {registNumLengthError ? (
          <Fieldset.Error>6자리 숫자로 입력해주세요.</Fieldset.Error>
        ) : (
          birthDateError && <Fieldset.Error>올바른 생년월일을 입력해주세요.</Fieldset.Error>
        )}
      </Fieldset.Wrapper>

      <div className="mb-5" />
      {!isPartnerPlatform && (
        <div className="flex">
          <Button.Text className="mr-6" onClick={handleClickFindeHometaxIDPassword}>
            아이디·비밀번호 찾기
          </Button.Text>
          <Button.Text onClick={handleClickCreateHometaxID}>홈택스 가입하기</Button.Text>
        </div>
      )}

      <Button.StickyBottom.Wrapper className="page-padding">
        <Button.StickyBottom.Button disabled={disableButton} state="primary" onClick={handleClickNext} loading={isInFlight}>
          다음
        </Button.StickyBottom.Button>
      </Button.StickyBottom.Wrapper>
    </div>
  );
};

export default CorporationHometaxAuthContent;
